import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import Sentry from 'config/sentry';

import {
  CreatePlaylistDocument,
  CreatePlaylistMutation,
  PlaylistImagesDocument,
  PlaylistImagesQuery,
} from 'graphql/generated/graphql';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import ReduxState from 'store/ReduxState';

export interface OnboardingPlaylistState {
  name?: string;
  playlistImageId?: string;
  storiesIds: string[];
  onboardingPlaylistId?: string;
}

export const initialOnboardingPlaylistState: OnboardingPlaylistState = {
  storiesIds: [],
};

export const onboardingPlaylistSlice = createSlice({
  name: 'onboardingPlaylist',
  initialState: initialOnboardingPlaylistState,
  reducers: {
    setOnboardingPlaylistId: (
      state,
      action: PayloadAction<
        Pick<OnboardingPlaylistState, 'onboardingPlaylistId'>
      >,
    ) => {
      state.onboardingPlaylistId = action.payload.onboardingPlaylistId;
    },
    setData: (
      state,
      action: PayloadAction<
        Pick<OnboardingPlaylistState, 'name' | 'playlistImageId' | 'storiesIds'>
      >,
    ) => {
      state.name = action.payload.name;
      state.playlistImageId = action.payload.playlistImageId;
      state.storiesIds = action.payload.storiesIds;
    },
    reset: () => initialOnboardingPlaylistState,
  },
});

export const getOnboardingPlaylistData = ({
  onboardingPlaylist,
}: {
  onboardingPlaylist: OnboardingPlaylistState;
}) => onboardingPlaylist;

export const storeOnboardingPlaylist = createAsyncThunk<
  void,
  {
    name: string;
    storiesIds: string[];
  },
  { state: ReduxState }
>(
  'onboardingPlaylist/storeOnboardingPlaylist',
  async ({ name, storiesIds }, { dispatch, getState }) => {
    const {
      auth: { isAuthenticated, headers },
      translations: { locale },
    } = getState();

    const queryOptions = buildGraphaelQueryOptions(
      isAuthenticated,
      headers,
      locale,
    );

    const { data } = await apolloClient.query<PlaylistImagesQuery>({
      query: PlaylistImagesDocument,
      ...queryOptions,
    });

    dispatch(
      setData({
        name,
        storiesIds,
        playlistImageId: data.playlistImages[0].id,
      }),
    );
  },
);

export const createOnboardingPlaylistIfPresent = createAsyncThunk<
  void,
  void,
  { state: ReduxState }
>(
  'onboardingPlaylist/createOnboardingPlaylistIfPresent',
  async (_, { dispatch, getState }) => {
    const {
      auth: { isAuthenticated, headers },
      translations: { locale },
      onboardingPlaylist: { name, storiesIds, playlistImageId },
    } = getState();

    if (storiesIds.length <= 0) return;

    const queryOptions = buildGraphaelQueryOptions(
      isAuthenticated,
      headers,
      locale,
    );

    const { data, errors } = await apolloClient.mutate<CreatePlaylistMutation>({
      mutation: CreatePlaylistDocument,
      ...queryOptions,
      variables: {
        input: {
          name,
          storiesIds,
          playlistImageId,
        },
      },
    });

    if (data?.createPlaylist) {
      dispatch(reset());
      dispatch(
        setOnboardingPlaylistId({
          onboardingPlaylistId: data.createPlaylist.id,
        }),
      );
    } else {
      errors?.forEach((e) => Sentry.captureException(e));
    }
  },
);

export const {
  setData,
  reset,
  setOnboardingPlaylistId,
} = onboardingPlaylistSlice.actions;

export default onboardingPlaylistSlice.reducer;
